if (!customElements.get('kik-countto')) {
    class KikCountTo extends HTMLElement {
        static get observedAttributes() {
            return ['to', 'from', 'duration', 'delay', 'decimals', 'prefix', 'suffix', 'separator'];
        }

        constructor() {
            super();
            this._shadow = this.attachShadow({ mode: 'open' });
            this._shadow.innerHTML = `
            <style>
                :host {
                display: inline-block;
                font-variant-numeric: tabular-nums;
                }
                .display {
                display: inline-block;
                font-feature-settings: 'tnum';
                font-kerning: none;
                }
                @media (prefers-reduced-motion: reduce) {
                :host {
                    opacity: 1 !important;
                }
                }
            </style>
            <span class="display" part="display"></span>
            `;
            this._display = this._shadow.querySelector('.display');
            this._target = 0;
            this._startVal = 0;
            this._duration = 2000;
            this._delay = 0;
            this._decimals = 0;
            this._prefix = '';
            this._suffix = '';
            this._separator = ',';
            this._started = false;
            this._raf = 0;
            this._observer = null;
            this._reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        }

        connectedCallback() {
            this._applyAttrs();
            this._render();
            this._setupObserver();
            if (this._reduceMotion.matches) {
            this._display.textContent = this._format(this._target);
            }
        }

        disconnectedCallback() {
            if (this._observer) {
            this._observer.disconnect();
            this._observer = null;
            }
            if (this._raf) {
            cancelAnimationFrame(this._raf);
            this._raf = 0;
            }
        }

        attributeChangedCallback() {
            this._applyAttrs();
            this._render();
        }

        _applyAttrs() {
            this._target = parseFloat(this.getAttribute('to') || '0') || 0;
            this._startVal = parseFloat(this.getAttribute('from') || '0') || 0;
            this._duration = parseFloat(this.getAttribute('duration') || '2') * 1000;
            this._delay = parseFloat(this.getAttribute('delay') || '0') * 1000;
            this._decimals = Math.max(0, parseInt(this.getAttribute('decimals') || '0'));
            this._prefix = this.getAttribute('prefix') || '';
            this._suffix = this.getAttribute('suffix') || '';
            this._separator = this.getAttribute('separator') || ',';
        }

        _render() {
            if (this._reduceMotion.matches) {
            this._display.textContent = this._format(this._target);
            return;
            }
            this._display.textContent = this._format(this._startVal);
        }

        _format(n) {
            let s = n.toFixed(this._decimals);
            if (this._separator && this._decimals === 0) {
            s = s.replace(/\B(?=(\d{3})+(?!\d))/g, this._separator);
            }
            return `${this._prefix}${s}${this._suffix}`;
        }

        _setupObserver() {
            this._observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                this.start();
                this._observer.disconnect();
                this._observer = null;
            }
            }, { threshold: 0.5 });
            this._observer.observe(this);
        }

        start() {
            if (this._started || this._reduceMotion.matches) return;
            this._started = true;
            this._startTime = performance.now() + this._delay;
            this._animate();
        }

        _animate = (now = performance.now()) => {
            if (now < this._startTime) {
            this._raf = requestAnimationFrame(this._animate);
            return;
            }
            const elapsed = now - this._startTime;
            const progress = Math.min(elapsed / this._duration, 1);
            const eased = 1 - Math.pow(1 - progress, 2); // easeOutQuad
            const current = this._startVal + (this._target - this._startVal) * eased;
            this._display.textContent = this._format(current);
            if (progress < 1) {
            this._raf = requestAnimationFrame(this._animate);
            }
        }
    }

    customElements.define('kik-countto', KikCountTo);
}
